// Vector "painted storybook" drawing helpers. Every world object is drawn from
// shapes here so the look is fully reliable and warm. play.ts owns the ordering
// (depth-sorted by baseline y) and the day-cycle tint; these just paint.
import type { BitmapFont, Draw } from '../engine/webgpu.js';
import { C, CI, REGION } from './config.js';
import type { WorldTextures } from './assets/textures.js';
import type { Struct } from './world.js';
import type { NodeDef } from './world.js';

export type Facing = 'down' | 'up' | 'left' | 'right';

// ── tiny shape helpers ───────────────────────────────────────────────────────
function ellipse(d: Draw, cx: number, cy: number, rx: number, ry: number, color: string, alpha = 1): void {
  const pts: { x: number; y: number }[] = [];
  const N = 16;
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2;
    pts.push({ x: cx + Math.cos(a) * rx, y: cy + Math.sin(a) * ry });
  }
  d.fill(pts, color, alpha);
}

function roundRect(d: Draw, x: number, y: number, w: number, h: number, color: string, alpha = 1): void {
  d.rect(x, y, w, h, color, alpha);
}

// ── text layout ──────────────────────────────────────────────────────────────
/**
 * Greedy word-wrap to a pixel width, using the font's own baked metrics rather
 * than a characters-times-guess estimate. Returns at least one line. A single
 * word wider than `maxW` is left overlong on its own line instead of being
 * broken mid-word — better a long word than a hyphen in "Masterwork".
 */
export function wrapText(font: BitmapFont, text: string, maxW: number, scale = 1): string[] {
  const words = text.split(' ').filter((w) => w.length > 0);
  if (words.length === 0) return [''];
  const lines: string[] = [];
  let line = words[0];
  for (let i = 1; i < words.length; i++) {
    const next = `${line} ${words[i]}`;
    if (font.measure(next, scale) <= maxW) line = next;
    else { lines.push(line); line = words[i]; }
  }
  lines.push(line);
  return lines;
}

// ── shadow ───────────────────────────────────────────────────────────────────
export function shadow(d: Draw, x: number, y: number, r: number): void {
  ellipse(d, x, y, r, r * 0.42, '#2c2417', 0.18);
}

// ── faces ────────────────────────────────────────────────────────────────────
/** A brow. Symmetric and gently ARCHED — a straight line angled down toward the
 *  nose reads as a frown at this size, which is the last thing this game wants.
 *  Sits well clear of the eye; a brow resting on the lid reads as a glower. */
function brow(d: Draw, ex: number, y: number, r: number, s: number, col: string, alpha = 0.5): void {
  d.poly([
    { x: ex - r * 1.15, y: y + 0.7 * s },
    { x: ex, y: y - 0.6 * s },
    { x: ex + r * 1.15, y: y + 0.7 * s },
  ], 0.9 * s, col, alpha);
}

/** A mouth: a gentle upward curve when smiling, otherwise a soft short line. */
function mouth(d: Draw, x: number, y: number, w: number, s: number, smiling: boolean): void {
  const col = shade(C.skinDark, -32);
  if (smiling) {
    d.poly([
      { x: x - w, y: y - 0.7 * s },
      { x: x - w * 0.38, y: y + 1.2 * s },
      { x: x + w * 0.38, y: y + 1.2 * s },
      { x: x + w, y: y - 0.7 * s },
    ], 1.4 * s, col, 0.8);
  } else {
    d.line(x - w * 0.62, y, x + w * 0.62, y, 1.3 * s, col, 0.6);
  }
}

// ── a person (Joseph, Mary, Jesus, villagers) ────────────────────────────────
export interface PersonOpts {
  robe: string; tunic?: string; skin?: string; hair?: string;
  beard?: boolean; gray?: boolean; scale?: number;
  facing?: Facing; bob?: number; armSwing?: number;
  action?: string;   // 'idle'|'walk'|'chop'|'saw'|'plane'|'hammer'|'measure'|'pray'|'carry'|'sit'
  carry?: string;    // 'wood'|'stone'|'timber' — carried in the arms
  veil?: string;     // Mary
  /** Front-facing smile — Joseph. Off by default; a whole village grinning is
   *  worse than a village of neutral faces. */
  smile?: boolean;
  /** A sash falling from the belt, to break up a flat robe. */
  sash?: boolean;
}

export function person(d: Draw, x: number, yFeet: number, o: PersonOpts): void {
  const s = o.scale ?? 1;
  const skin = o.skin ?? C.skin;
  const hair = o.hair ?? (o.gray ? C.hairGray : C.hair);
  const facing = o.facing ?? 'down';
  const bob = o.bob ?? 0;
  const y = yFeet + bob;

  const h = 78 * s;
  const headR = 12.5 * s;
  const headCY = y - h + headR;
  const shoulderY = headCY + headR + 3 * s;
  const topW = 24 * s, botW = 33 * s;
  const dir = facing === 'left' ? -1 : facing === 'right' ? 1 : 0;

  shadow(d, x, yFeet + 1, 19 * s);

  if (o.action === 'pray') { prayingPerson(d, x, y, o, s, skin, hair); return; }

  // legs / lower robe hem hint
  ellipse(d, x, y - 2 * s, botW * 0.5, 6 * s, shade(o.robe, -30));

  // body (robe) trapezoid
  const body = [
    { x: x - topW / 2, y: shoulderY },
    { x: x + topW / 2, y: shoulderY },
    { x: x + botW / 2, y: y - 2 * s },
    { x: x - botW / 2, y: y - 2 * s },
  ];
  d.fill(body, o.robe);
  // inner tunic stripe
  if (o.tunic) {
    const iw = topW * 0.5;
    d.fill([
      { x: x - iw / 2, y: shoulderY + 2 * s },
      { x: x + iw / 2, y: shoulderY + 2 * s },
      { x: x + iw * 0.62, y: y - 3 * s },
      { x: x - iw * 0.62, y: y - 3 * s },
    ], o.tunic);
  }
  // belt
  {
    // belt, and a sash falling from it — the robe was one flat block of colour
    const by = shoulderY + (h - headR * 2) * 0.42;
    d.rect(x - botW * 0.42, by, botW * 0.84, 5.5 * s, shade(o.robe, -45));
    d.rect(x - botW * 0.42, by, botW * 0.84, 1.6 * s, shade(o.robe, -14), 0.7);
    if (o.sash) {
      d.fill([
        { x: x + botW * 0.10, y: by },
        { x: x + botW * 0.26, y: by },
        { x: x + botW * 0.22, y: y - 8 * s },
        { x: x + botW * 0.08, y: y - 8 * s },
      ], shade(o.robe, -30));
      d.line(x + botW * 0.11, by + 4 * s, x + botW * 0.20, y - 10 * s, 1.1 * s, shade(o.robe, -52), 0.6);
    }
  }
  // robe outline
  d.poly([...body, body[0]], 1.6 * s, shade(o.robe, -55), 0.85);

  // arms
  const armLen = 20 * s, armW = 6.5 * s;
  const swing = Math.sin((o.armSwing ?? 0)) * 6 * s;
  drawArm(d, x - topW / 2 + 1 * s, shoulderY + 3 * s, armW, armLen, shade(o.robe, -20), -swing);
  drawArm(d, x + topW / 2 - 1 * s, shoulderY + 3 * s, armW, armLen, shade(o.robe, -20), swing);

  // action tool / carried goods (drawn in front of body)
  if (o.carry) carriedGoods(d, x, shoulderY + 6 * s, s, o.carry, dir);
  else if (o.action && o.action !== 'idle' && o.action !== 'walk') tool(d, x, shoulderY, headCY, s, dir, o.action);

  // neck
  d.rect(x - 3.5 * s, headCY + headR - 2 * s, 7 * s, 6 * s, skin);

  // head
  d.circle(x, headCY, headR, skin);
  if (facing === 'up') {
    // the back of the head — all hair, and deliberately NO face
    d.circle(x, headCY, headR, hair);
    d.circle(x, headCY - headR * 0.2, headR * 0.8, shade(hair, 12), 0.5);
  } else {
    // hair frames the face: cap on top/back, then face skin in front
    ellipse(d, x - dir * 2 * s, headCY - headR * 0.32, headR * 1.06, headR * 0.92, hair);
    ellipse(d, x + dir * 3 * s, headCY + headR * 0.18, headR * 0.84, headR * 0.88, skin);

    if (o.beard) {
      // a fuller silhouette with a lighter edge, so it still reads small
      ellipse(d, x + dir * 2 * s, headCY + headR * 0.76, headR * 0.98, headR * 0.82, shade(hair, -16));
      ellipse(d, x + dir * 2 * s, headCY + headR * 0.70, headR * 0.88, headR * 0.70, hair);
      ellipse(d, x + dir * 2 * s, headCY + headR * 0.60, headR * 0.70, headR * 0.44, shade(hair, 26), 0.55);
    }

    // ── the face. Eyes are ovals with a catchlight; a face without one reads
    // as blank at this size. Mouth is a short arc, and only Joseph smiles.
    const eyeR = 2.4 * s;
    const eyes: number[] = facing === 'down' ? [x - 4.4 * s, x + 4.4 * s] : [x + dir * 5 * s];
    for (const ex of eyes) {
      ellipse(d, ex, headCY + 0.4 * s, eyeR * 0.86, eyeR, shade(C.ink, 14));
      d.circle(ex - 0.5 * s, headCY - 0.4 * s, eyeR * 0.34, C.white, 0.9);   // catchlight
      // a brow, which is most of what gives a face any expression
      brow(d, ex, headCY - eyeR * 2.3, eyeR, s, shade(hair, 8), 0.5);
    }
    if (facing === 'down') {
      const my = headCY + headR * (o.beard ? 0.34 : 0.46);
      // a beard swallows a mouth drawn on top of it — clear a little skin first
      if (o.beard) ellipse(d, x, my + 1 * s, headR * 0.36, headR * 0.22, shade(skin, -4));
      mouth(d, x, my, headR * 0.30, s, !!o.smile);
    }
  }
  // a soft rim so the head separates from the floor behind it
  d.ring(x, headCY, headR, 1.2 * s, shade(skin, -46), 0.45);

  // ── the veil (Mary). It used to be one hard ellipse clamped over the head,
  // which read as a helmet. It now falls PAST the head onto the shoulders, with
  // a soft edge, so it drapes.
  if (o.veil) {
    const vd = shade(o.veil, -22);
    d.fill([                                    // the cloth falling to the shoulders
      { x: x - headR * 1.18, y: headCY - headR * 0.3 },
      { x: x + headR * 1.18, y: headCY - headR * 0.3 },
      { x: x + headR * 1.42, y: shoulderY + 8 * s },
      { x: x - headR * 1.42, y: shoulderY + 8 * s },
    ], vd);
    ellipse(d, x, headCY - 2 * s, headR * 1.24, headR * 1.12, o.veil);
    ellipse(d, x, headCY - headR * 0.5, headR * 0.9, headR * 0.4, shade(o.veil, 16), 0.6);
    // the opening, set lower so more face shows than before
    ellipse(d, x, headCY + 3 * s, headR * 0.86, headR * 0.92, skin);
    d.poly([                                    // one fold, where the cloth turns
      { x: x - headR * 1.0, y: headCY + headR * 0.5 },
      { x: x - headR * 0.7, y: shoulderY + 4 * s },
    ], 1.3 * s, vd, 0.7);
    if (facing !== 'up') {
      const eyeR = 2.0 * s;
      const eyes: number[] = facing === 'down' ? [x - 4.2 * s, x + 4.2 * s] : [x + dir * 4.6 * s];
      for (const ex of eyes) {
        ellipse(d, ex, headCY + 2.4 * s, eyeR * 0.86, eyeR, shade(C.ink, 14));
        d.circle(ex - 0.5 * s, headCY + 1.6 * s, eyeR * 0.34, C.white, 0.9);
        // raised, softened and arched — the old brow sloped into the nose and
        // made her look permanently cross
        brow(d, ex, headCY - eyeR * 0.5, eyeR, s, shade(C.hair, 30), 0.35);
      }
      if (facing === 'down') mouth(d, x, headCY + headR * 0.54, headR * 0.26, s, !!o.smile);
    }
    d.ring(x, headCY - 2 * s, headR * 1.24, 1.1 * s, vd, 0.35);
  }
}

function drawArm(d: Draw, x: number, y: number, w: number, len: number, color: string, dy: number): void {
  d.rect(x - w / 2, y + dy, w, len, color);
  d.circle(x, y + len + dy, w * 0.6, C.skin); // hand
}

function carriedGoods(d: Draw, x: number, y: number, s: number, kind: string, _dir: number): void {
  if (kind === 'stone') {
    d.circle(x, y + 8 * s, 10 * s, C.rock);
    d.circle(x - 5 * s, y + 6 * s, 6 * s, shade(C.rock, 20));
    d.circle(x + 5 * s, y + 9 * s, 6 * s, shade(C.rock, -20));
  } else {
    const col = kind === 'timber' ? C.timberLight : C.wood;
    for (let i = 0; i < 3; i++) {
      d.rect(x - 16 * s, y + 4 * s + i * 5 * s, 32 * s, 4 * s, i % 2 ? shade(col, -18) : col);
    }
  }
}

function tool(d: Draw, x: number, shoulderY: number, headCY: number, s: number, dir: number, action: string): void {
  const hx = x + (dir === 0 ? 10 : dir * 16) * s;
  const hy = shoulderY + 10 * s;
  switch (action) {
    case 'chop': {
      // an axe raised
      d.line(x + 12 * s, headCY - 6 * s, x + 20 * s, shoulderY + 12 * s, 3.2 * s, C.timberDark);
      d.fill([
        { x: x + 10 * s, y: headCY - 12 * s }, { x: x + 22 * s, y: headCY - 8 * s },
        { x: x + 20 * s, y: headCY }, { x: x + 12 * s, y: headCY - 2 * s },
      ], C.rock);
      break;
    }
    case 'saw':
      d.line(hx - 6 * s, hy, hx + 20 * s, hy - 3 * s, 3 * s, C.timberDark);
      d.line(hx - 6 * s, hy + 3 * s, hx + 20 * s, hy, 2 * s, '#cfc9b0');
      break;
    case 'hammer':
      d.line(x + 8 * s, headCY - 2 * s, hx, hy, 2.6 * s, C.timberDark);
      d.rect(x + 6 * s, headCY - 8 * s, 10 * s, 7 * s, C.rockDark);
      break;
    case 'plane':
      d.rect(hx - 8 * s, hy - 4 * s, 20 * s, 8 * s, C.timber);
      break;
    case 'measure':
      d.line(hx - 10 * s, hy, hx + 14 * s, hy, 2.4 * s, C.gold);
      break;
    default:
      d.circle(hx, hy, 3 * s, C.timberDark);
  }
}

function prayingPerson(d: Draw, x: number, y: number, o: PersonOpts, s: number, skin: string, hair: string): void {
  // kneeling — lower, hands together, head bowed
  const h = 54 * s;
  const headR = 12 * s;
  const headCY = y - h + headR;
  ellipse(d, x, y - 2 * s, 26 * s, 10 * s, o.robe);
  const body = [
    { x: x - 14 * s, y: headCY + headR }, { x: x + 14 * s, y: headCY + headR },
    { x: x + 22 * s, y: y - 2 * s }, { x: x - 22 * s, y: y - 2 * s },
  ];
  d.fill(body, o.robe);
  d.poly([...body, body[0]], 1.5 * s, shade(o.robe, -55), 0.8);
  // hands together in front
  d.circle(x, headCY + headR + 6 * s, 5 * s, skin);
  d.rect(x - 4 * s, headCY + headR + 2 * s, 8 * s, 5 * s, skin);
  d.rect(x - 3.5 * s, headCY + headR - 2 * s, 7 * s, 5 * s, skin);
  d.circle(x, headCY, headR, skin);
  ellipse(d, x, headCY - headR * 0.35, headR * 1.02, headR * 0.85, hair);
  if (o.beard) ellipse(d, x, headCY + headR * 0.7, headR * 0.8, headR * 0.65, hair);
  if (o.veil) { ellipse(d, x, headCY - 1 * s, headR * 1.25, headR * 1.15, o.veil); ellipse(d, x, headCY + 2 * s, headR * 0.85, headR * 0.9, skin); }
}

// ── colour shading ───────────────────────────────────────────────────────────
export function shade(hex: string, amt: number): string {
  const h = hex.replace('#', '');
  const n = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  let r = parseInt(n.slice(0, 2), 16), g = parseInt(n.slice(2, 4), 16), b = parseInt(n.slice(4, 6), 16);
  r = Math.max(0, Math.min(255, r + amt)); g = Math.max(0, Math.min(255, g + amt)); b = Math.max(0, Math.min(255, b + amt));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}


// ══ SURFACE DETAIL ═══════════════════════════════════════════════════════════
// The handmade layer. Every random-looking decision below is a hash of the
// object's OWN coordinates, so a given tree is the same tree every time you
// walk back to it — variety without flicker, and without a stored seed.

let TEX: WorldTextures | null = null;
/** play.ts hands over the baked noise sheets at setup; until then all the
 *  texture helpers are silent no-ops and the world just draws flat. */
export function useTextures(t: WorldTextures): void { TEX = t; }

/** Deterministic 0..1 from two numbers. */
export function hash01(a: number, b: number): number {
  const s = Math.sin(a * 127.1 + b * 311.7) * 43758.5453;
  return s - Math.floor(s);
}
/** Deterministic value in [lo, hi). */
function vary(a: number, b: number, lo: number, hi: number): number {
  return lo + hash01(a, b) * (hi - lo);
}
/** Deterministic pick from a list. */
function pick<T>(a: number, b: number, list: readonly T[]): T {
  return list[Math.min(list.length - 1, Math.floor(hash01(a, b) * list.length))];
}

/** Fine tooth over a surface — sawn timber, quarried stone, coarse cloth. */
export function grain(d: Draw, x: number, y: number, w: number, h: number, color: string, alpha: number, tile = 42): void {
  if (!TEX) return;
  d.sprite(TEX.grain, x, y, { w, h, tint: color, alpha, uvRepeat: { x: Math.max(1, w / tile), y: Math.max(1, h / tile) } });
}

/** Broad weathering blotches — plaster, worn earth, old thatch. */
export function mottle(d: Draw, x: number, y: number, w: number, h: number, color: string, alpha: number, tile = 190): void {
  if (!TEX) return;
  d.sprite(TEX.mottle, x, y, { w, h, tint: color, alpha, uvRepeat: { x: Math.max(1, w / tile), y: Math.max(1, h / tile) } });
}

/** World-locked ground texture. The quad is the CAMERA's rect, so it travels
 *  with the view; offsetting the UVs by the view origin nails the pattern to
 *  the ground instead of letting it swim along with the player. `sheet` picks
 *  broad patches ('mottle') or fine tooth ('grain'). */
export function groundTexture(
  d: Draw, vx: number, vy: number, vw: number, vh: number,
  color: string, alpha: number, tile: number, sheet: 'mottle' | 'grain' = 'mottle',
): void {
  if (!TEX) return;
  d.sprite(sheet === 'grain' ? TEX.grain : TEX.mottle, vx, vy, {
    w: vw, h: vh, tint: color, alpha,
    uvRepeat: { x: vw / tile, y: vh / tile },
    uvScroll: { x: vx / tile, y: vy / tile },
  });
}

/** A region wash whose chosen edges dissolve instead of ending on a straight
 *  line. Drawn as nested rects at a fraction of the alpha each, so the interior
 *  accumulates the full wash and the outermost band only a slice of it —
 *  nesting whole rects (rather than banding each edge separately) is what keeps
 *  corners from double-darkening where two feathered edges meet. Soft lobes
 *  then straddle each faded edge, because a stepped ramp is smooth but still
 *  perfectly straight, and it is the straightness that reads as a seam. */
export function featherRect(
  d: Draw, x: number, y: number, w: number, h: number,
  color: string, alpha: number, feather: number,
  sides: { top?: boolean; right?: boolean; bottom?: boolean; left?: boolean } = {},
): void {
  const n = REGION.steps;
  // Per-pass alpha picked so n composited passes land on exactly `alpha`:
  // the middle of the region keeps the colour it has always had.
  const a = 1 - Math.pow(1 - alpha, 1 / n);
  for (let i = 0; i < n; i++) {
    const ins = (feather * i) / n;
    const l = x + (sides.left ? ins : 0), t = y + (sides.top ? ins : 0);
    const r = x + w - (sides.right ? ins : 0), b = y + h - (sides.bottom ? ins : 0);
    if (r > l && b > t) d.rect(l, t, r - l, b - t, color, a);
  }
  const rad = feather * REGION.lobe;
  const lobes = (px: number, py: number, dx: number, dy: number, len: number, seed: number): void => {
    const count = Math.max(2, Math.round(len / (rad * 1.7)));
    for (let i = 0; i < count; i++) {
      const k = (i + 0.5) / count;
      const j = hash01(seed, i);
      d.circle(px + dx * len * k, py + dy * len * k, rad * (0.5 + j * 0.85), color, a * 1.25);
    }
  };
  const off = feather * 0.35;
  if (sides.top)    lobes(x, y + off, 1, 0, w, 11);
  if (sides.bottom) lobes(x, y + h - off, 1, 0, w, 23);
  if (sides.left)   lobes(x + off, y, 0, 1, h, 37);
  if (sides.right)  lobes(x + w - off, y, 0, 1, h, 51);
}

/** A patch of warm light fallen through leaves. */
export function dapple(d: Draw, cx: number, cy: number, r: number, seed: number, alpha = 0.15): void {
  if (!TEX) return;
  d.sprite(TEX.dapple, cx - r, cy - r * 0.55, { w: r * 2, h: r * 1.1, tint: C.goldLight, alpha, rot: hash01(seed, 17) * 6.283 });
}

/** A two-stage shadow: a wide soft skirt under a tighter core. Cheaper to read
 *  as "sitting on the ground" than the single hard ellipse it replaces. */
export function softShadow(d: Draw, x: number, y: number, r: number): void {
  ellipse(d, x, y + 1.5, r * 1.10, r * 0.48, '#2c2417', 0.09);
  ellipse(d, x, y, r * 0.80, r * 0.36, '#2c2417', 0.17);
}

/** Warm top-edge light — the cheapest way to make a shape feel sunlit. */
function sunEdge(d: Draw, x: number, y: number, w: number, h: number, alpha = 0.28): void {
  d.rect(x, y, w, h, C.goldLight, alpha);
}

/** A board: fill, grain, a couple of long figure lines, sometimes a knot, and
 *  a lit top arris. The building block for everything wooden. */
export function plank(d: Draw, x: number, y: number, w: number, h: number, col: string, seed: number): void {
  d.rect(x, y, w, h, col);
  grain(d, x, y, w, h, shade(col, -42), 0.15, 30);
  const lines = 2 + Math.floor(hash01(seed, 1) * 3);
  for (let i = 0; i < lines; i++) {
    const gy = y + h * ((i + 0.7) / (lines + 0.5));
    d.line(x + vary(seed, i + 9, 1, w * 0.2), gy, x + w - vary(seed, i + 3, 1, w * 0.2), gy + vary(seed, i, -0.7, 0.7), 0.9, shade(col, -36), 0.45);
  }
  if (hash01(seed, 5) > 0.55) {
    const kx = x + vary(seed, 6, w * 0.2, w * 0.8), ky = y + h * 0.5;
    d.circle(kx, ky, Math.min(2.6, h * 0.3), shade(col, -48), 0.7);
    d.circle(kx, ky, Math.min(1.2, h * 0.14), shade(col, -62), 0.55);
  }
  sunEdge(d, x, y, w, Math.max(1, h * 0.16), 0.2);
}

/** Hand-stippled grit for shapes a rectangular noise sprite cannot sit inside —
 *  rocks, canopies, anything with a silhouette. Scattered in an ellipse. */
export function speckle(d: Draw, cx: number, cy: number, rx: number, ry: number, color: string, n: number, seed: number, alpha = 0.45): void {
  for (let i = 0; i < n; i++) {
    const a = hash01(seed, i) * 6.283;
    const rr = Math.sqrt(hash01(seed, i + 50));
    d.circle(cx + Math.cos(a) * rx * rr, cy + Math.sin(a) * ry * rr, vary(seed, i + 90, 0.6, 1.7), color, alpha);
  }
}

// ── small clutter: the props that say somebody lives and works here ──────────
export function clayJar(d: Draw, x: number, yBase: number, s = 1, tone: string = C.roofDark): void {
  softShadow(d, x, yBase, 9 * s);
  ellipse(d, x, yBase - 9 * s, 8 * s, 10 * s, tone);
  ellipse(d, x - 2.4 * s, yBase - 11 * s, 3 * s, 5 * s, shade(tone, 26), 0.5);
  d.rect(x - 3.4 * s, yBase - 21 * s, 6.8 * s, 5 * s, shade(tone, -14));
  d.rect(x - 4.6 * s, yBase - 22 * s, 9.2 * s, 2.4 * s, shade(tone, 14));
}

export function basketProp(d: Draw, x: number, yBase: number, s = 1): void {
  softShadow(d, x, yBase, 10 * s);
  const w = 18 * s, h = 13 * s;
  d.fill([
    { x: x - w / 2, y: yBase - h }, { x: x + w / 2, y: yBase - h },
    { x: x + w * 0.38, y: yBase }, { x: x - w * 0.38, y: yBase },
  ], shade(C.wood, -18));
  for (let i = 1; i < 3; i++) d.line(x - w * 0.46, yBase - h + (h * i) / 3, x + w * 0.46, yBase - h + (h * i) / 3, 1.1, shade(C.wood, -34), 0.55);
  d.rect(x - w / 2, yBase - h - 1.6 * s, w, 2.6 * s, shade(C.wood, 12));
}

export function firewoodProp(d: Draw, x: number, yBase: number, s = 1, seed = 0): void {
  softShadow(d, x, yBase, 13 * s);
  for (let i = 0; i < 4; i++) {
    const row = Math.floor(i / 2), col = i % 2;
    const lx = x - 11 * s + col * 12 * s + vary(seed, i, -1.5, 1.5);
    const ly = yBase - 5 * s - row * 6.5 * s;
    d.rect(lx, ly - 5 * s, 11 * s, 5.5 * s, i % 2 ? C.timber : shade(C.timber, -16));
    d.circle(lx + 11 * s, ly - 2.2 * s, 2.7 * s, shade(C.wood, vary(seed, i + 2, -6, 16)));
  }
}

export function sackProp(d: Draw, x: number, yBase: number, s = 1): void {
  softShadow(d, x, yBase, 9 * s);
  d.fill([
    { x: x - 8 * s, y: yBase }, { x: x + 8 * s, y: yBase },
    { x: x + 6 * s, y: yBase - 15 * s }, { x: x - 6 * s, y: yBase - 15 * s },
  ], C.parchmentEdge);
  grain(d, x - 8 * s, yBase - 16 * s, 16 * s, 16 * s, shade(C.parchmentEdge, -40), 0.2, 18);
  d.rect(x - 4 * s, yBase - 19 * s, 8 * s, 4.5 * s, shade(C.parchmentEdge, -18));
  d.line(x - 5 * s, yBase - 15 * s, x + 5 * s, yBase - 15 * s, 1.4, shade(C.parchmentEdge, -34), 0.7);
}

export function potStack(d: Draw, x: number, yBase: number, s = 1, seed = 0): void {
  softShadow(d, x, yBase, 11 * s);
  const tones: string[] = [C.roof, C.roofDark, shade(C.roof, 18)];
  ellipse(d, x, yBase - 6 * s, 9 * s, 6.5 * s, pick(seed, 1, tones));
  ellipse(d, x + 2 * s, yBase - 15 * s, 6.5 * s, 5 * s, pick(seed, 2, tones));
  ellipse(d, x - 1 * s, yBase - 22 * s, 4.5 * s, 3.6 * s, pick(seed, 3, tones));
}

export function bucketProp(d: Draw, x: number, yBase: number, s = 1): void {
  softShadow(d, x, yBase, 8 * s);
  d.fill([
    { x: x - 7 * s, y: yBase - 13 * s }, { x: x + 7 * s, y: yBase - 13 * s },
    { x: x + 5.4 * s, y: yBase }, { x: x - 5.4 * s, y: yBase },
  ], shade(C.timber, -6));
  d.rect(x - 7 * s, yBase - 13 * s, 14 * s, 2.2 * s, shade(C.timber, 20));
  d.line(x - 6 * s, yBase - 7 * s, x + 6 * s, yBase - 7 * s, 1.2, shade(C.timberDark, 10), 0.7);
  d.poly([{ x: x - 6 * s, y: yBase - 13 * s }, { x, y: yBase - 19 * s }, { x: x + 6 * s, y: yBase - 13 * s }], 1.3, C.rockDark, 0.75);
}

export function benchProp(d: Draw, x: number, yBase: number, s = 1, seed = 0): void {
  softShadow(d, x, yBase, 16 * s);
  plank(d, x - 17 * s, yBase - 13 * s, 34 * s, 6 * s, C.timber, seed);
  d.rect(x - 14 * s, yBase - 7 * s, 4.5 * s, 7 * s, C.timberDark);
  d.rect(x + 10 * s, yBase - 7 * s, 4.5 * s, 7 * s, C.timberDark);
}

/** Wood curls and dust — the floor of a shop that has been used today. */
export function shavings(d: Draw, x: number, y: number, seed: number, n = 7, spread = 34): void {
  for (let i = 0; i < n; i++) {
    const sx = x + vary(seed, i, -spread, spread);
    const sy = y + vary(seed, i + 40, -spread * 0.32, spread * 0.32);
    if (hash01(seed, i + 80) > 0.45) {
      d.ring(sx, sy, vary(seed, i + 5, 1.8, 3.4), 1.1, shade(C.wood, 20), 0.75);
    } else {
      d.circle(sx, sy, vary(seed, i + 7, 0.8, 1.7), shade(C.wood, 8), 0.6);
    }
  }
}

/** Herbs or onions hung to dry against a wall. */
export function herbBundle(d: Draw, x: number, y: number, s = 1, seed = 0): void {
  d.line(x, y, x, y + 5 * s, 1.2, C.timberDark, 0.8);
  for (let i = -1; i <= 1; i++) {
    d.line(x, y + 5 * s, x + i * 3.4 * s, y + 16 * s, 2.2 * s, pick<string>(seed, i + 4, [C.olive, C.leafDark, shade(C.olive, 14)]), 0.95);
  }
  d.circle(x, y + 5 * s, 2 * s, shade(C.timber, 10));
}

/** Scatter a few seeded props along a building's base — same building, same
 *  clutter on every visit, but no two buildings dressed alike. Kept to the
 *  outer thirds so nothing ever blocks a doorway. */
export function buildingClutter(d: Draw, x: number, w: number, baseY: number, seed: number): void {
  const n = 2 + Math.floor(hash01(seed, 41) * 3);
  for (let i = 0; i < n; i++) {
    const right = hash01(seed, i * 13 + 2) > 0.5;
    const off = vary(seed, i, -10, w * 0.26);
    const px = right ? x + w - off : x + off;
    const py = baseY - vary(seed, i + 2, 0, 10);
    const s = vary(seed, i + 8, 0.82, 1.18);
    switch (pick(seed, i * 7 + 3, ['jar', 'basket', 'firewood', 'sack', 'pots', 'bench', 'bucket'] as const)) {
      case 'jar': clayJar(d, px, py, s, pick<string>(seed, i, [C.roofDark, C.roof, shade(C.roof, -22)])); break;
      case 'basket': basketProp(d, px, py, s); break;
      case 'firewood': firewoodProp(d, px, py, s, seed + i); break;
      case 'sack': sackProp(d, px, py, s); break;
      case 'pots': potStack(d, px, py, s, seed + i); break;
      case 'bench': benchProp(d, px, py, s, seed + i); break;
      default: bucketProp(d, px, py, s); break;
    }
  }
}

// ── structures ───────────────────────────────────────────────────────────────
export function drawStruct(d: Draw, st: Struct, workshopLevel: number, t: number, night: boolean): void {
  switch (st.kind) {
    case 'home':      drawHouse(d, st, C.stoneWall, true); drawHomeInterior(d, st); break;
    case 'house':     drawHouse(d, st, st.tint ?? C.stoneWall, false); break;
    case 'synagogue': drawSynagogue(d, st); break;
    case 'workshop':  drawWorkshop(d, st, workshopLevel, night); break;
    case 'well':      drawWell(d, st); break;
    case 'stall':     drawStall(d, st, t); break;
    case 'oliveDecor':drawOliveTree(d, st.x + st.w / 2, st.y + st.h); break;
    case 'bush':      drawBush(d, st.x + st.w / 2, st.y + st.h); break;
    case 'garden':    drawGarden(d, st, t); break;
    case 'trough':    drawTrough(d, st, t); break;
    case 'store':     drawStore(d, st); break;
    case 'byre':      drawByre(d, st); break;
    default: break;
  }
  if (night && (st.kind === 'home' || st.kind === 'house' || st.kind === 'synagogue' || st.kind === 'stall')) {
    lampGlow(d, st.x + st.w * 0.5, st.y + st.h * 0.72, 46, t);
  }
}

function drawHouse(d: Draw, st: Struct, wall: string, open: boolean): void {
  const { x, y, w, h } = st;
  const seed = x * 0.37 + y * 0.11;          // this house's own personality
  const wallY = y + h * 0.32, wallH = h * 0.68;
  softShadow(d, x + w / 2, y + h, w * 0.5);

  // ── walls: plaster over stone, sun on one face, cool shade on the other
  d.rect(x, wallY, w, wallH, wall);
  mottle(d, x, wallY, w, wallH, shade(wall, -30), 0.16, 150);
  grain(d, x, wallY, w, wallH, shade(wall, -36), 0.07, 30);
  d.rect(x, wallY, w * 0.30, wallH, C.goldLight, 0.10);
  d.rect(x + w * 0.80, wallY, w * 0.20, wallH, C.dusk, 0.08);
  d.rect(x, wallY, w, 6, shade(wall, 22));
  sunEdge(d, x, wallY, w, 2.5, 0.35);

  // ── stone courses, jittered so no two walls are ruled the same way
  const courses = 4 + Math.floor(hash01(seed, 2) * 3);
  const ch = wallH / courses;
  for (let i = 1; i < courses; i++) {
    const cy = wallY + ch * i + vary(seed, i, -1.6, 1.6);
    d.line(x + 2, cy, x + w - 2, cy, 1.1, shade(wall, -30), 0.28);
    const joints = 2 + Math.floor(hash01(seed, i * 5) * 3);
    for (let j = 0; j < joints; j++) {
      const jx = x + w * ((j + (i % 2 ? 0.34 : 0.68)) / joints) + vary(seed, i * 3 + j, -8, 8);
      if (jx > x + 6 && jx < x + w - 6) d.line(jx, cy, jx, cy - ch, 1.0, shade(wall, -28), 0.20);
    }
  }
  d.rect(x, y + h - 7, w, 7, shade(wall, -26), 0.45);   // dirt-worn base course
  d.poly([{ x, y: y + h }, { x: x + w, y: y + h }], 2, shade(wall, -30), 0.5);

  // ── roof: terracotta pantiles, warm along the ridge
  const apex = { x: x + w / 2, y };
  const eL = { x: x - 8, y: y + h * 0.36 }, eR = { x: x + w + 8, y: y + h * 0.36 };
  const roofTone = shade(C.roof, Math.round(vary(seed, 9, -16, 14)));
  d.fill([eL, apex, eR], roofTone);
  d.fill([apex, eR, { x: x + w / 2, y: y + h * 0.36 }], shade(roofTone, -26));
  for (let i = 1; i <= 5; i++) {                        // tile courses
    const f = i / 6;
    d.line(apex.x + (eL.x - apex.x) * f, apex.y + (eL.y - apex.y) * f,
           apex.x + (eR.x - apex.x) * f, apex.y + (eR.y - apex.y) * f,
           1.2, shade(roofTone, -30), 0.32);
  }
  for (let i = 1; i < 7; i++) {                          // pantile ribs
    const ex = eL.x + (eR.x - eL.x) * (i / 7);
    d.line(apex.x + (ex - apex.x) * 0.14, apex.y + (eL.y - apex.y) * 0.14, ex, eL.y, 1.0, shade(roofTone, -22), 0.20);
  }
  mottle(d, x - 8, y, w + 16, h * 0.36, shade(roofTone, -34), 0.15, 120);
  d.line(eL.x, eL.y, eR.x, eR.y, 3, shade(roofTone, -34));
  d.line(apex.x - w * 0.26, apex.y + h * 0.10, apex.x, apex.y + 1, 2, C.goldLight, 0.35);

  if (!open) {
    // ── door, recessed, with a plank face
    const dx = x + w * 0.4, dw = w * 0.2, dy = y + h * 0.55, dh = h * 0.45;
    d.rect(dx - 3, dy - 3, dw + 6, dh + 3, shade(wall, -34));
    d.rect(dx, dy, dw, dh, C.timberDark);
    grain(d, dx, dy, dw, dh, shade(C.timberDark, -28), 0.28, 16);
    for (let i = 1; i < 3; i++) d.line(dx + (dw * i) / 3, dy + 2, dx + (dw * i) / 3, dy + dh, 1, shade(C.timberDark, -22), 0.6);
    d.circle(dx + dw * 0.82, dy + dh * 0.5, 1.8, C.gold, 0.9);
    // ── windows: shuttered on some houses, mullioned on others
    for (const f of [0.12, 0.72]) {
      const wx = x + w * f, ww = w * 0.16, wh = h * 0.2, wy = y + h * 0.48;
      d.rect(wx - 2, wy - 2, ww + 4, wh + 4, shade(wall, -30));
      d.rect(wx, wy, ww, wh, '#3b2c1e');
      d.rect(wx, wy, ww, wh * 0.4, C.lamp, 0.12);
      if (hash01(seed, f * 100) > 0.5) {
        d.rect(wx - 3.5, wy - 1, 4, wh + 2, C.timber);
        d.rect(wx + ww - 0.5, wy - 1, 4, wh + 2, C.timber);
      } else {
        d.line(wx + ww / 2, wy, wx + ww / 2, wy + wh, 1.2, C.timberDark, 0.8);
      }
    }
  }

  // ── the things a household leaves against its own wall
  buildingClutter(d, x, w, y + h - 2, seed);
  if (hash01(seed, 61) > 0.45) herbBundle(d, x + w * vary(seed, 62, 0.22, 0.78), y + h * 0.42, vary(seed, 63, 0.8, 1.15), seed);
}

/** The doorway on the front of Joseph's house. It sits at the RIGHT-HAND end of
 *  the wall, not the middle, because he always comes home from the workshop to
 *  the east — the door is placed to meet him rather than making him round the
 *  building. The threshold in interiors.ts sits directly under this opening. */
function drawHomeInterior(d: Draw, st: Struct): void {
  const { x, y, w, h } = st;
  const dx = x + w * 0.64, dw = w * 0.30;
  d.rect(dx - 4, y + h * 0.5 - 4, dw + 8, h * 0.5 + 4, shade(C.stoneWall, -34));   // reveal
  d.rect(dx, y + h * 0.5, dw, h * 0.5, shade('#5a3a24', 10));
  d.rect(dx + 3, y + h * 0.62, dw - 6, h * 0.36, '#c98a4a', 0.5);
  // warmth spilling out of the opening, so the door reads as a way in
  d.circle(dx + dw * 0.5, y + h * 0.86, 12, C.lamp, 0.5);
  d.circle(dx + dw * 0.5, y + h * 1.0, 20, C.lamp, 0.14);
}

function drawSynagogue(d: Draw, st: Struct): void {
  const { x, y, w, h } = st;
  shadow(d, x + w / 2, y + h, w * 0.5);
  d.rect(x, y + h * 0.28, w, h * 0.72, C.stoneWall);
  d.rect(x, y + h * 0.28, w, 8, shade(C.stoneWall, 24));
  // columns
  for (let i = 0; i < 4; i++) {
    const cx = x + w * (0.16 + i * 0.23);
    d.rect(cx, y + h * 0.34, 16, h * 0.62, shade(C.stoneWall, -18));
    d.rect(cx - 3, y + h * 0.34, 22, 6, shade(C.stoneWall, 10));
  }
  // pediment roof
  d.fill([{ x: x - 10, y: y + h * 0.3 }, { x: x + w / 2, y: y - 6 }, { x: x + w + 10, y: y + h * 0.3 }], shade(C.stoneWall, -8));
  d.line(x - 10, y + h * 0.3, x + w + 10, y + h * 0.3, 3, shade(C.stoneWall, -30));
  d.circle(x + w / 2, y + h * 0.12, 8, C.gold, 0.85); // small emblem
}

function drawWell(d: Draw, st: Struct): void {
  const { x, y, w, h } = st;
  const cx = x + w / 2, cy = y + h * 0.6;
  shadow(d, cx, y + h, w * 0.55);
  d.circle(cx, cy, w * 0.5, C.stoneDark);
  d.circle(cx, cy, w * 0.42, shade(C.stoneWall, -6));
  d.circle(cx, cy, w * 0.3, C.waterDeep);
  d.circle(cx, cy - 2, w * 0.26, C.water);
  // posts + roof
  d.rect(cx - w * 0.44, y - 6, 8, h * 0.7, C.timberDark);
  d.rect(cx + w * 0.36, y - 6, 8, h * 0.7, C.timberDark);
  d.fill([{ x: cx - w * 0.6, y: y + 6 }, { x: cx, y: y - 26 }, { x: cx + w * 0.6, y: y + 6 }], C.roof);
}

function drawStall(d: Draw, st: Struct, t: number): void {
  const { x, y, w, h } = st;
  shadow(d, x + w / 2, y + h, w * 0.5);
  d.rect(x, y + h * 0.4, w, h * 0.6, C.timber);
  d.rect(x + 4, y + h * 0.5, w - 8, h * 0.2, shade(C.timber, 20)); // goods board
  // striped awning (deform-free, drawn)
  const stripe = st.tint ?? C.cloth;
  for (let i = 0; i < 5; i++) {
    d.rect(x + i * (w / 5), y + h * 0.12 + Math.sin(t * 1.2 + i) * 1.5, w / 5, h * 0.3, i % 2 ? stripe : C.parchment);
  }
  d.rect(x - 4, y - 6, 6, h, C.timberDark);
  d.rect(x + w - 2, y - 6, 6, h, C.timberDark);
  // little pots/jars on the counter
  d.circle(x + w * 0.25, y + h * 0.44, 6, C.roofDark);
  d.circle(x + w * 0.6, y + h * 0.44, 7, shade(C.roof, 20));
}


// ── the timber store ─────────────────────────────────────────────────────────
/** The walled back room behind the open-air shop. Small, plain and timber-clad:
 *  the shop out front is the showpiece, this is where the wood lives. The door
 *  faces SOUTH, back toward the workbench Joseph works at, so he meets it
 *  head-on rather than walking round a blank wall. */
function drawStore(d: Draw, st: Struct): void {
  const { x, y, w, h } = st;
  const seed = x * 0.43 + y * 0.29;
  softShadow(d, x + w / 2, y + h, w * 0.5);

  // timber-boarded walls
  const wallY = y + h * 0.30, wallH = h * 0.70;
  d.rect(x, wallY, w, wallH, C.timber);
  grain(d, x, wallY, w, wallH, shade(C.timber, -40), 0.20, 24);
  for (let i = 1; i < 6; i++) {
    d.line(x + (w * i) / 6, wallY, x + (w * i) / 6, y + h, 1.4, shade(C.timber, -30), 0.5);
  }
  d.rect(x, wallY, w, 5, shade(C.timber, 22));
  sunEdge(d, x, wallY, w, 2.5, 0.30);

  // a low shingled roof
  const apex = { x: x + w / 2, y };
  const eL = { x: x - 7, y: wallY + 4 }, eR = { x: x + w + 7, y: wallY + 4 };
  d.fill([eL, apex, eR], shade(C.timberDark, 12));
  d.fill([apex, eR, { x: x + w / 2, y: wallY + 4 }], shade(C.timberDark, -14));
  for (let i = 1; i <= 3; i++) {
    const f = i / 4;
    d.line(apex.x + (eL.x - apex.x) * f, apex.y + (eL.y - apex.y) * f,
           apex.x + (eR.x - apex.x) * f, apex.y + (eR.y - apex.y) * f,
           1.2, shade(C.timberDark, -26), 0.4);
  }
  d.line(eL.x, eL.y, eR.x, eR.y, 3, shade(C.timberDark, -28));
  d.line(apex.x - w * 0.24, apex.y + h * 0.10, apex.x, apex.y + 1, 2, C.goldLight, 0.32);

  // The doorway is on the EAST return of the shed, facing the byre. It used to
  // face south, straight onto the walking route from the compound to the
  // grove, and swallowed anyone heading north.
  const dx = x + w * 0.70, dw = w * 0.28;
  d.rect(dx - 4, y + h * 0.46 - 4, dw + 8, h * 0.54 + 4, shade(C.timberDark, -22));
  d.rect(dx, y + h * 0.46, dw, h * 0.54, shade('#4a2f1c', 6));
  d.rect(dx + 3, y + h * 0.56, dw - 6, h * 0.42, '#c98a4a', 0.35);
  d.circle(x + w + 6, y + h * 0.72, 16, C.lamp, 0.12);   // light spilling east
  // a board and a bucket left against the wall
  plank(d, x + w * 0.10, y + h - 26, w * 0.26, 8, C.wood, seed);
  bucketProp(d, x + w * 0.40, y + h - 4, 0.9);
}

// ── the village garden ───────────────────────────────────────────────────────
/** A worked plot: turned earth, four rows of different crops, a stone edging,
 *  and the tools whoever is tending it has left standing in the soil. Nothing
 *  here is harvestable — it is the village feeding itself, not a resource. */
function drawGarden(d: Draw, st: Struct, t: number): void {
  const { x, y, w, h } = st;
  const seed = x * 0.29 + y * 0.23;

  d.fill([                                        // turned earth, corners eased
    { x: x + 8, y }, { x: x + w - 8, y }, { x: x + w, y: y + 10 },
    { x: x + w, y: y + h - 8 }, { x: x + w - 10, y: y + h },
    { x: x + 10, y: y + h }, { x, y: y + h - 10 }, { x, y: y + 10 },
  ], shade(C.sand, -40));
  mottle(d, x, y, w, h, shade(C.timberDark, -10), 0.20, 90);
  grain(d, x, y, w, h, shade(C.sand, -54), 0.10, 26);

  const rows = 4;
  for (let r = 0; r < rows; r++) {
    const ry = y + h * ((r + 0.85) / (rows + 0.7));
    d.line(x + 12, ry + 7, x + w - 12, ry + 7, 5, shade(C.sand, -52), 0.35);   // furrow
    const kind = r % 3;                            // leafy / herb / staked vine
    const n = 6 + Math.floor(hash01(seed, r) * 2);
    for (let i = 0; i < n; i++) {
      const px = x + 22 + ((w - 44) * i) / (n - 1) + vary(seed, r * 9 + i, -3, 3);
      const py = ry + vary(seed, r * 5 + i, -2, 2);
      const sc = vary(seed, r * 7 + i, 0.85, 1.2);
      if (kind === 0) {                            // leafy greens
        d.circle(px - 4 * sc, py - 3 * sc, 5 * sc, C.leafDark);
        d.circle(px + 4 * sc, py - 4 * sc, 5.5 * sc, C.leaf);
        d.circle(px, py - 7 * sc, 5 * sc, C.leafLight);
      } else if (kind === 1) {                     // low herbs
        for (let k = -1; k <= 1; k++) {
          d.line(px + k * 2.5 * sc, py, px + k * 4.5 * sc, py - 10 * sc, 1.7, C.olive);
        }
        if (hash01(seed, r * 3 + i) > 0.6) d.circle(px, py - 11 * sc, 2, C.goldLight, 0.9);
      } else {                                     // staked vine
        d.line(px, py, px + 1.5 * sc, py - 22 * sc, 1.6, C.timber);
        d.circle(px - 3 * sc, py - 10 * sc, 4 * sc, C.leaf);
        d.circle(px + 4 * sc, py - 16 * sc, 4.5 * sc, C.leafDark);
        if (hash01(seed, r * 11 + i) > 0.55) d.circle(px + 2 * sc, py - 13 * sc, 2.6 * sc, C.cloth, 0.95);
      }
    }
  }

  // a low stone edging, laid by hand and not quite even
  for (let i = 0; i < 14; i++) {
    const f = i / 13;
    d.circle(x + 6 + (w - 12) * f, y + h - 3 + vary(seed, i, -2, 2), vary(seed, i + 20, 3, 5), C.stoneDark, 0.9);
  }
  // the gardener's things, left standing
  hoeProp(d, x + w - 26, y + h - 10, 0.9);
  clayJar(d, x + 20, y + h - 8, 0.85, C.roofDark);
  d.line(x + 44, y + h - 8, x + 47, y + h - 26, 2, C.timber);   // a stake with twine
  d.line(x + 47, y + h - 26, x + 62, y + h - 22, 1, C.parchmentEdge, 0.8);
}

/** A watering trough at the pasture edge. */
function drawTrough(d: Draw, st: Struct, t: number): void {
  const { x, y, w, h } = st;
  softShadow(d, x + w / 2, y + h, w * 0.5);
  d.fill([
    { x, y }, { x: x + w, y }, { x: x + w - 5, y: y + h }, { x: x + 5, y: y + h },
  ], C.stoneDark);
  d.rect(x + 4, y + 3, w - 8, h * 0.42, shade(C.water, -6));
  d.rect(x + 4, y + 3, w - 8, 2.5, C.water, 0.8);
  d.circle(x + w * 0.32, y + h * 0.3, 2.2, C.white, 0.35 + 0.15 * Math.sin(t * 1.4));
  speckle(d, x + w / 2, y + h * 0.7, w * 0.4, h * 0.3, shade(C.stoneDark, -20), 7, x, 0.35);
}

// ── the evolving workshop (open-air carpenter's shop) ────────────────────────
function drawWorkshop(d: Draw, st: Struct, level: number, night: boolean): void {
  const { x, y, w, h } = st;
  const cx = x + w / 2, groundY = y + h;
  const seed = x * 0.43 + y * 0.29;
  // soft worn-earth floor (semi-transparent so Joseph draws cleanly over it)
  ellipse(d, cx, groundY - 24, w * 0.52, 50, shade(C.sand, -18), 0.55);
  ellipse(d, cx, groundY - 24, w * 0.4, 38, shade(C.sand, -6), 0.4);
  mottle(d, cx - w * 0.52, groundY - 74, w * 1.04, 100, shade(C.sand, -30), 0.13, 110);
  speckle(d, cx, groundY - 26, w * 0.42, 34, shade(C.wood, 14), 16, seed, 0.30);   // sawdust

  // back frame: two posts + a timber-and-canvas shade roof
  if (level >= 1) {
    const pL = x + 34, pR = x + w - 44, topY = y + 30, beamY = y + 52;
    d.rect(pL - 5, beamY, 10, groundY - 40 - beamY, C.timberDark);
    d.rect(pR - 5, beamY, 10, groundY - 40 - beamY, C.timberDark);
    grain(d, pL - 5, beamY, 10, groundY - 40 - beamY, shade(C.timberDark, -30), 0.34, 12);
    grain(d, pR - 5, beamY, 10, groundY - 40 - beamY, shade(C.timberDark, -30), 0.34, 12);
    d.fill([{ x: x + 4, y: beamY + 6 }, { x: cx, y: topY }, { x: x + w - 4, y: beamY + 6 }], C.timber);
    for (let i = 1; i < 6; i++) {                    // shingle courses on the shade roof
      const f = i / 6;
      d.line(cx + (x + 4 - cx) * f, topY + (beamY + 6 - topY) * f,
             cx + (x + w - 4 - cx) * f, topY + (beamY + 6 - topY) * f, 1.1, shade(C.timber, -30), 0.3);
    }
    d.line(cx - w * 0.2, topY + 8, cx, topY + 1, 2, C.goldLight, 0.32);
    d.line(x + 4, beamY + 6, x + w - 4, beamY + 6, 4, C.timberDark);
    d.rect(pL, beamY, pR - pL, 8, C.timberDark); // cross-beam
    // canvas awning at the front eave
    d.fill([{ x: pL - 6, y: beamY + 4 }, { x: pR + 6, y: beamY + 4 }, { x: pR - 2, y: beamY + 22 }, { x: pL + 2, y: beamY + 22 }], C.parchment, 0.9);
  }

  // lumber pile (always present) — grows with the shop
  const lx = x + w * 0.82, ly = groundY - 40;
  for (let i = 0; i < 3 + Math.min(level, 4); i++) {
    plank(d, lx - 28 + vary(seed, i, -2, 2), ly - i * 7, 56, 6, i % 2 ? C.wood : shade(C.wood, -16), seed + i);
  }
  shavings(d, lx - 10, ly + 14, seed + 3, 6, 26);
  d.circle(lx - 28, ly - 3, 4, shade(C.wood, 18)); d.circle(lx + 28, ly - 3, 4, shade(C.wood, 18));

  // the workbench
  const bx = x + w * 0.25, by = groundY - 96;
  if (level >= 1) {
    plank(d, bx - 36, by, 72, 13, C.timberLight, seed + 11);
    d.rect(bx - 36, by, 72, 4, shade(C.timberLight, 20));
    shavings(d, bx, by + 58, seed + 5, 9, 30);
    d.rect(bx - 32, by + 13, 8, 40, C.timberDark);
    d.rect(bx + 24, by + 13, 8, 40, C.timberDark);
    d.rect(bx + 26, by - 5, 9, 11, C.rockDark);           // vice
    d.line(bx - 20, by - 3, bx + 6, by - 3, 2, '#d8d2ba'); // a plank being worked
  } else {
    d.rect(bx - 30, by + 30, 60, 9, shade(C.wood, -10));  // rough plank on stones
    d.circle(bx - 24, by + 42, 8, C.rock); d.circle(bx + 24, by + 42, 8, C.rock);
    d.rect(lx - 20, ly - 2, 40, 6, C.wood);
  }
  // level 2: tool rack on the cross-beam
  if (level >= 2) {
    const ry = y + 64;
    d.rect(x + w * 0.42, ry, 60, 6, C.timberDark);
    d.line(x + w * 0.44, ry + 6, x + w * 0.44, ry + 22, 2.6, C.rockDark);
    d.rect(x + w * 0.5, ry + 6, 5, 16, C.timberDark);
    d.rect(x + w * 0.55, ry + 6, 4, 13, C.gold);
    d.line(x + w * 0.6, ry + 6, x + w * 0.6, ry + 20, 2.6, '#cfc9b0');
  }
  // level 3: a proper saw bench
  if (level >= 3) { d.rect(bx + 52, by + 18, 34, 12, C.timber); d.rect(bx + 54, by + 30, 6, 16, C.timberDark); d.rect(bx + 80, by + 30, 6, 16, C.timberDark); d.line(bx + 54, by + 16, bx + 84, by + 16, 2, '#d8d2ba'); }
  // level 4: wood storage shelves
  if (level >= 4) { d.rect(x + w * 0.6, groundY - 118, 60, 46, C.timberDark); for (let i = 0; i < 3; i++) d.rect(x + w * 0.61, groundY - 114 + i * 14, 56, 5, C.wood); }
  // level 5: stoneworking corner
  if (level >= 5) { d.rect(x + w * 0.34, groundY - 40, 44, 20, C.rockDark); d.circle(x + w * 0.38, groundY - 42, 9, C.rock); d.rect(x + w * 0.5, groundY - 44, 5, 20, C.timberDark); }
  // level 6: finished work & gifts of thanks
  if (level >= 6) { d.rect(bx - 58, groundY - 58, 18, 26, C.timberLight); d.rect(bx - 56, groundY - 58, 14, 5, shade(C.timberLight, 18)); d.circle(lx, ly - 44, 6, C.cloth); d.circle(lx + 9, ly - 48, 5, C.gold); }
  // level 7: a beautiful, ordered shop
  if (level >= 7) { d.rect(cx - 2, y + 56, 4, 16, C.timberDark); d.circle(cx, y + 74, 7, C.lamp, 0.95); d.rect(bx - 62, groundY - 78, 20, 30, C.timber); d.line(bx - 60, groundY - 78, bx - 44, groundY - 78, 2, C.timberDark); }

  if (night && level >= 1) lampGlow(d, cx, groundY - 60, 66, 0);
}

// ── resource nodes ───────────────────────────────────────────────────────────
export function drawTree(d: Draw, x: number, yBase: number, kind: 'tree' | 'log' | 'branch', depleted: boolean): void {
  if (kind === 'branch') {
    shadow(d, x, yBase, 16);
    if (depleted) { d.line(x - 10, yBase, x + 6, yBase - 2, 2, shade(C.sand, -20)); return; }
    d.line(x - 14, yBase - 2, x + 14, yBase + 2, 5, C.timber);
    d.line(x - 4, yBase - 6, x + 8, yBase - 12, 3.5, shade(C.timber, 14));
    d.line(x - 2, yBase, x - 10, yBase - 8, 3, C.leafDark);
    return;
  }
  if (kind === 'log') {
    shadow(d, x, yBase, 22);
    d.rect(x - 26, yBase - 14, 52, 16, depleted ? shade(C.timber, -30) : C.timber);
    d.circle(x - 26, yBase - 6, 8, depleted ? shade(C.wood, -20) : C.wood);
    d.circle(x + 26, yBase - 6, 8, depleted ? shade(C.wood, -20) : C.wood);
    d.circle(x + 26, yBase - 6, 3, C.timberDark);
    return;
  }
  // a full tree — no two alike: trunk height, clump count, sizes and tones all
  // fall out of the tree's own coordinates.
  const seed = x * 0.29 + yBase * 0.13;
  dapple(d, x + vary(seed, 50, -20, 20), yBase - 6, vary(seed, 51, 22, 34), seed, 0.14);
  softShadow(d, x, yBase, 34);
  const th = vary(seed, 1, 54, 70);
  d.fill([                                          // root flare
    { x: x - 15, y: yBase + 1 }, { x: x - 8, y: yBase - 16 },
    { x: x + 8, y: yBase - 16 }, { x: x + 15, y: yBase + 1 },
  ], C.timberDark);
  d.rect(x - 8, yBase - th, 16, th, C.timberDark);
  d.rect(x - 8, yBase - th, 5, th, shade(C.timberDark, 18));
  grain(d, x - 8, yBase - th, 16, th, shade(C.timberDark, -30), 0.32, 13);
  if (depleted) {
    d.rect(x - 12, yBase - 18, 24, 18, C.timber);
    grain(d, x - 12, yBase - 18, 24, 18, shade(C.timber, -34), 0.3, 12);
    d.circle(x, yBase - 18, 12, C.wood);
    d.ring(x, yBase - 18, 7.5, 1.1, shade(C.wood, -22), 0.8);   // growth rings
    d.ring(x, yBase - 18, 4, 1, shade(C.wood, -22), 0.7);
    d.circle(x, yBase - 18, 1.6, shade(C.wood, -34), 0.8);
    return;
  }
  // Canopy in three deliberate tiers — dark base, mid mass, sunlit crown. The
  // ORDER is what carries the depth, so only the counts, offsets and radii vary
  // per tree; scrambling the tones instead just flattens it.
  const crownY = yBase - th - 20;
  const tier = (n: number, tone: string, dy: number, r0: number, r1: number, salt: number): void => {
    for (let i = 0; i < n; i++) {
      const a = (i / n) * 6.283 + hash01(seed, salt + i) * 1.4;
      d.circle(
        x + Math.cos(a) * vary(seed, salt + i + 20, 10, 24),
        crownY + dy + Math.sin(a) * vary(seed, salt + i + 30, 4, 12),
        vary(seed, salt + i + 10, r0, r1),
        tone,
      );
    }
  };
  tier(2 + Math.floor(hash01(seed, 3) * 2), C.leafDark, 9, 26, 36, 0);
  tier(2 + Math.floor(hash01(seed, 4) * 2), C.leaf, -2, 24, 34, 100);
  tier(1 + Math.floor(hash01(seed, 5) * 2), C.leafLight, -15, 16, 25, 200);
  speckle(d, x, crownY, 30, 18, shade(C.leafDark, -6), 10, seed, 0.14);
  d.circle(x + vary(seed, 41, -10, 8), crownY - vary(seed, 42, 16, 24), vary(seed, 43, 9, 15), C.goldLight, 0.24);
}

export function drawRock(d: Draw, x: number, yBase: number, depleted: boolean): void {
  const seed = x * 0.41 + yBase * 0.17;
  softShadow(d, x, yBase, 26);
  if (depleted) {
    d.circle(x, yBase - 6, vary(seed, 1, 8, 12), shade(C.rock, -10));
    d.circle(x + vary(seed, 2, 5, 11), yBase - 4, vary(seed, 3, 5, 8), C.rockDark);
    speckle(d, x, yBase - 5, 12, 5, shade(C.rockDark, -18), 6, seed, 0.4);
    return;
  }
  // Same silhouette, jittered per rock, so a quarry isn't a row of clones.
  const tone = shade(C.rock, Math.round(vary(seed, 4, -14, 12)));
  const body = [
    { x: x - 24, y: yBase }, { x: x - 14, y: yBase - 22 }, { x: x + 6, y: yBase - 26 },
    { x: x + 24, y: yBase - 10 }, { x: x + 18, y: yBase },
  ].map((pt, i) => ({ x: pt.x + vary(seed, i, -4, 4), y: pt.y + vary(seed, i + 9, -3.5, 2) }));
  d.fill(body, tone);
  d.fill([body[1], body[2], { x: x - 2, y: yBase - 12 }], shade(tone, 22));     // lit facet
  d.fill([body[2], body[3], { x: x + 8, y: yBase - 8 }], shade(tone, -16));     // shaded facet
  speckle(d, x, yBase - 12, 17, 9, shade(tone, -26), 12, seed, 0.35);
  if (hash01(seed, 31) > 0.55) {                                                // lichen
    d.circle(x + vary(seed, 12, -14, 12), yBase - vary(seed, 13, 3, 12), vary(seed, 14, 3, 6), C.leafDark, 0.25);
  }
  d.poly(body.slice(0, 4), 1.6, C.rockDark, 0.65);
}

export function drawFlax(d: Draw, x: number, yBase: number, depleted: boolean): void {
  shadow(d, x, yBase, 16);
  const col = depleted ? shade(C.olive, -20) : C.olive;
  for (let i = -2; i <= 2; i++) {
    d.line(x + i * 5, yBase, x + i * 5 + i * 2, yBase - (depleted ? 10 : 26), 2.4, col);
    if (!depleted) d.circle(x + i * 5 + i * 2, yBase - 26, 3, C.gold);
  }
}

export function drawClothStand(d: Draw, x: number, yBase: number, depleted: boolean): void {
  shadow(d, x, yBase, 20);
  d.rect(x - 22, yBase - 6, 44, 6, C.timberDark);
  d.rect(x - 20, yBase - 4, 8, 20, C.timberDark);
  d.rect(x + 12, yBase - 4, 8, 20, C.timberDark);
  if (!depleted) {
    d.rect(x - 18, yBase - 34, 16, 30, C.cloth);
    d.rect(x + 2, yBase - 30, 16, 26, shade(C.cloth, 24));
  }
}

export function drawOliveTree(d: Draw, x: number, yBase: number): void {
  const seed = x * 0.31 + yBase * 0.19;
  dapple(d, x + vary(seed, 50, -14, 14), yBase - 4, vary(seed, 51, 16, 26), seed, 0.12);
  softShadow(d, x, yBase, 26);
  const th = vary(seed, 1, 38, 50);
  // olives grow gnarled — a slight lean and a side branch read as old
  const lean = vary(seed, 2, -4, 4);
  d.fill([
    { x: x - 7, y: yBase }, { x: x - 5 + lean, y: yBase - th },
    { x: x + 5 + lean, y: yBase - th }, { x: x + 7, y: yBase },
  ], C.timber);
  grain(d, x - 7, yBase - th, 14, th, shade(C.timber, -36), 0.3, 12);
  d.line(x + lean, yBase - th * 0.55, x + lean - 9, yBase - th * 0.95, 3.5, C.timber);
  const crown = yBase - th - 14;
  d.circle(x - 14 + lean, crown + 6, vary(seed, 3, 18, 24), C.olive);
  d.circle(x + 14 + lean, crown + 10, vary(seed, 4, 16, 22), shade(C.olive, 14));
  d.circle(x + lean, crown - 8, vary(seed, 5, 20, 26), shade(C.olive, 6));
  d.circle(x - 4 + lean, crown - 4, vary(seed, 6, 13, 18), C.leafLight);
  speckle(d, x + lean, crown, 24, 16, shade(C.olive, -20), 10, seed, 0.25);
  d.circle(x + lean + 4, crown - 14, 8, C.goldLight, 0.24);
}

function drawBush(d: Draw, x: number, yBase: number): void {
  const seed = x * 0.53 + yBase * 0.23;
  softShadow(d, x, yBase, 16);
  const sc = vary(seed, 1, 0.85, 1.25);
  d.circle(x - 8 * sc, yBase - 10, 14 * sc, C.leafDark);
  d.circle(x + 8 * sc, yBase - 12, 15 * sc, C.leaf);
  d.circle(x, yBase - 18 * sc, 14 * sc, C.leafLight);
  speckle(d, x, yBase - 13, 15 * sc, 9 * sc, shade(C.leafDark, -10), 8, seed, 0.25);
  if (hash01(seed, 7) > 0.5) {                       // berries on some bushes
    for (let i = 0; i < 3; i++) {
      d.circle(x + vary(seed, i + 20, -13, 13), yBase - vary(seed, i + 30, 8, 22), 1.9, pick<string>(seed, i, [C.cloth, '#b8493a', C.gold]), 0.95);
    }
  }
  d.circle(x - 3, yBase - 22 * sc, 6 * sc, C.goldLight, 0.22);
}


// ══ ANIMALS ══════════════════════════════════════════════════════════════════
// Ambient only — none of these can be interacted with, and none of them ask to
// be. Motion is a sine of the clock plus a per-animal phase, so there is no
// state to keep, nothing to update, and nothing that can drift out of sync.

/** Four short legs under a body, with a walking scissor if `gait` is moving. */
function legs(d: Draw, x: number, yBase: number, spread: number, len: number, col: string, gait: number, wob: number): void {
  for (let i = 0; i < 4; i++) {
    const lx = x + (i < 2 ? -spread : spread) * (i % 2 ? 0.62 : 1);
    const sw = Math.sin(gait + i * 1.7) * wob;
    d.line(lx, yBase - len, lx + sw, yBase, 2.4, col);
  }
}

export function drawGoat(d: Draw, x: number, yBase: number, t: number, seed: number): void {
  const coat = pick<string>(seed, 1, [C.parchmentEdge, shade(C.sand, -14), shade(C.timber, 34), C.stoneDark]);
  const graze = 0.5 + 0.5 * Math.sin(t * 0.7 + seed);      // head dips to the grass
  const gait = t * 1.6 + seed;
  softShadow(d, x, yBase, 15);
  legs(d, x, yBase, 9, 13, shade(coat, -46), gait, 1.6);
  ellipse(d, x, yBase - 19, 15, 9, coat);
  speckle(d, x, yBase - 19, 13, 7, shade(coat, -26), 5, seed, 0.3);
  d.line(x + 14, yBase - 24, x + 19, yBase - 29, 2.2, shade(coat, -20));   // tail
  // head, dropping toward the grass as it grazes
  const hx = x - 16, hy = yBase - 24 + graze * 12;
  ellipse(d, hx, hy, 7, 5.5, shade(coat, 10));
  d.circle(hx - 5, hy + 1, 2.6, shade(coat, 16));                          // muzzle
  d.line(hx - 1, hy - 4, hx + 5, hy - 10, 1.8, shade(coat, -50));          // horns
  d.line(hx + 2, hy - 4, hx + 8, hy - 9, 1.8, shade(coat, -50));
  d.circle(hx - 2, hy - 1, 1, C.ink, 0.8);
}

export function drawSheep(d: Draw, x: number, yBase: number, t: number, seed: number): void {
  const wool = pick<string>(seed, 2, [C.white, C.parchment, shade(C.parchment, -12)]);
  const graze = 0.5 + 0.5 * Math.sin(t * 0.55 + seed * 1.7);
  softShadow(d, x, yBase, 15);
  legs(d, x, yBase, 8, 11, shade(C.rockDark, -10), t * 1.3 + seed, 1.2);
  // the fleece: a cluster, not an ellipse — that is what reads as wool
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * 6.283 + hash01(seed, i);
    d.circle(x + Math.cos(a) * 8, yBase - 20 + Math.sin(a) * 4.5, vary(seed, i, 7, 9.5), wool);
  }
  d.circle(x, yBase - 21, 9, shade(wool, 8));
  const hx = x - 14, hy = yBase - 22 + graze * 10;
  ellipse(d, hx, hy, 5.5, 5, shade(C.rockDark, 6));                        // dark face
  d.circle(hx - 3, hy + 1.5, 2.2, shade(C.rockDark, 16));
  d.circle(hx - 1.5, hy - 1, 0.9, C.ink, 0.8);
  d.line(hx + 3, hy - 3, hx + 7, hy - 4, 2, shade(C.rockDark, 2));         // ear
}

export function drawChicken(d: Draw, x: number, yBase: number, t: number, seed: number): void {
  const body = pick<string>(seed, 3, [shade(C.parchmentEdge, 8), C.roofDark, shade(C.timber, 26), C.white]);
  const peck = Math.max(0, Math.sin(t * 2.2 + seed * 3));                  // quick head dips
  softShadow(d, x, yBase, 7);
  d.line(x - 2, yBase - 5, x - 2, yBase, 1.6, C.gold);
  d.line(x + 3, yBase - 5, x + 3, yBase, 1.6, C.gold);
  ellipse(d, x, yBase - 10, 8, 6.5, body);
  d.line(x + 7, yBase - 13, x + 13, yBase - 17, 3, shade(body, -22));      // tail feathers
  const hy = yBase - 17 + peck * 8;
  d.circle(x - 6, hy, 4, shade(body, 12));
  d.circle(x - 6, hy - 4, 2, C.cloth, 0.95);                               // comb
  d.line(x - 9.5, hy + 0.5, x - 13, hy + 1.5, 1.8, C.gold);               // beak
  d.circle(x - 7.5, hy - 0.6, 0.8, C.ink, 0.85);
}

export function drawDonkey(d: Draw, x: number, yBase: number, t: number, seed: number): void {
  const coat = shade(C.rock, Math.round(vary(seed, 4, -12, 8)));
  const sway = Math.sin(t * 0.5 + seed) * 2;
  softShadow(d, x, yBase, 20);
  legs(d, x, yBase, 12, 20, shade(coat, -34), t * 0.9 + seed, 0.9);
  ellipse(d, x, yBase - 30, 21, 12, coat);
  speckle(d, x, yBase - 30, 18, 9, shade(coat, -20), 6, seed, 0.25);
  d.line(x + 19, yBase - 36, x + 25, yBase - 26, 2.4, shade(coat, -40));   // tail
  d.rect(x - 8, yBase - 42, 18, 5, shade(coat, -34));                      // mane
  // long head, ears, and a slow patient sway
  const hx = x - 20 + sway, hy = yBase - 38;
  ellipse(d, hx, hy + 5, 7, 10, coat);
  d.circle(hx - 1, hy + 14, 4.6, shade(coat, -16));                        // muzzle
  d.line(hx - 4, hy - 5, hx - 7, hy - 16, 3.2, coat);                      // ears
  d.line(hx + 3, hy - 5, hx + 6, hy - 16, 3.2, coat);
  d.circle(hx - 3.5, hy + 3, 1.1, C.ink, 0.85);
}

/** The ox: broader and heavier than the donkey, built for pulling stone. */
export function drawOx(d: Draw, x: number, yBase: number, t: number, seed: number, load = false): void {
  const coat = pick<string>(seed, 6, [shade(C.timberDark, 22), '#6b5540', shade(C.rockDark, -8)]);
  const sway = Math.sin(t * 0.4 + seed) * 1.6;
  softShadow(d, x, yBase, 25);
  legs(d, x, yBase, 15, 22, shade(coat, -34), t * 0.7 + seed, 0.7);
  ellipse(d, x, yBase - 34, 26, 15, coat);                                  // deep barrel
  ellipse(d, x - 4, yBase - 44, 15, 8, shade(coat, 12));                    // shoulder hump
  speckle(d, x, yBase - 34, 22, 11, shade(coat, -22), 7, seed, 0.25);
  d.line(x + 24, yBase - 40, x + 31, yBase - 24, 2.6, shade(coat, -40));    // tail
  if (load) stoneLoad(d, x, yBase - 52, seed);
  // heavy low head, and horns that curve out
  const hx = x - 25 + sway, hy = yBase - 40;
  ellipse(d, hx, hy + 8, 9, 11, coat);
  d.circle(hx - 2, hy + 17, 5.6, shade(coat, -18));                         // muzzle
  d.poly([{ x: hx - 3, y: hy - 3 }, { x: hx - 10, y: hy - 10 }, { x: hx - 16, y: hy - 8 }], 2.6, C.parchmentEdge);
  d.poly([{ x: hx + 5, y: hy - 3 }, { x: hx + 12, y: hy - 10 }, { x: hx + 18, y: hy - 7 }], 2.6, C.parchmentEdge);
  d.circle(hx - 4, hy + 5, 1.2, C.ink, 0.85);
}

/** Panniers — the mark of a working animal, and what tells it apart from the
 *  village's ambient donkeys at a glance. */
export function panniers(d: Draw, x: number, yBase: number, seed: number, load = false): void {
  d.rect(x - 14, yBase - 40, 28, 5, C.timberDark);                          // the strap
  for (const side of [-1, 1]) {
    const bx = x + side * 17;
    d.fill([
      { x: bx - 8, y: yBase - 38 }, { x: bx + 8, y: yBase - 38 },
      { x: bx + 6, y: yBase - 20 }, { x: bx - 6, y: yBase - 20 },
    ], shade(C.wood, -16));
    d.line(bx - 7, yBase - 32, bx + 7, yBase - 32, 1.2, shade(C.wood, -36), 0.6);
    if (load) { d.rect(bx - 6, yBase - 44, 12, 7, C.wood); d.rect(bx - 6, yBase - 44, 12, 2, shade(C.wood, 18)); }
  }
  void seed;
}

/** A stone riding on the ox's back. */
function stoneLoad(d: Draw, x: number, y: number, seed: number): void {
  d.fill([
    { x: x - 16, y: y + 8 }, { x: x - 10, y: y - 8 }, { x: x + 8, y: y - 10 },
    { x: x + 17, y: y + 2 }, { x: x + 12, y: y + 9 },
  ], shade(C.rock, 6));
  speckle(d, x, y, 12, 6, shade(C.rock, -26), 6, seed, 0.35);
}

/** A bushel of cut logs — three Wood, and too heavy to shoulder alone. */
export function drawLogpile(d: Draw, x: number, yBase: number, depleted: boolean): void {
  const seed = x * 0.37 + yBase * 0.21;
  softShadow(d, x, yBase, 30);
  if (depleted) {                       // the binding rope, left behind
    d.line(x - 16, yBase - 3, x + 16, yBase - 5, 2.4, C.parchmentEdge, 0.8);
    d.circle(x + 16, yBase - 5, 3, shade(C.parchmentEdge, -20), 0.8);
    return;
  }
  for (let row = 0; row < 3; row++) {
    const n = 3 - row;
    for (let i = 0; i < n; i++) {
      const lx = x - (n - 1) * 13 + i * 26 + vary(seed, row * 3 + i, -2, 2);
      const ly = yBase - 8 - row * 17;
      d.rect(lx - 12, ly - 15, 24, 16, row % 2 ? C.timber : shade(C.timber, -12));
      grain(d, lx - 12, ly - 15, 24, 16, shade(C.timber, -40), 0.2, 12);
      d.circle(lx + 12, ly - 7, 8, shade(C.wood, vary(seed, row + i, -4, 16)));
      d.circle(lx + 12, ly - 7, 3, shade(C.wood, -26), 0.7);
    }
  }
  d.line(x - 26, yBase - 28, x + 26, yBase - 30, 2.6, C.parchmentEdge, 0.9);  // bound
  d.line(x - 24, yBase - 14, x + 24, yBase - 16, 2.6, C.parchmentEdge, 0.9);
}

/** A large stone — three Stone, and only the ox will shift it. */
export function drawBoulder(d: Draw, x: number, yBase: number, depleted: boolean): void {
  const seed = x * 0.29 + yBase * 0.31;
  softShadow(d, x, yBase, 38);
  if (depleted) {
    d.circle(x - 6, yBase - 6, 9, shade(C.rock, -12));
    d.circle(x + 9, yBase - 4, 7, C.rockDark);
    speckle(d, x, yBase - 5, 16, 6, shade(C.rockDark, -18), 7, seed, 0.4);
    return;
  }
  const tone = shade(C.rock, Math.round(vary(seed, 4, -10, 10)));
  const body = [
    { x: x - 38, y: yBase }, { x: x - 30, y: yBase - 34 }, { x: x - 8, y: yBase - 46 },
    { x: x + 20, y: yBase - 40 }, { x: x + 38, y: yBase - 16 }, { x: x + 30, y: yBase },
  ].map((pt, i) => ({ x: pt.x + vary(seed, i, -5, 5), y: pt.y + vary(seed, i + 9, -4, 3) }));
  d.fill(body, tone);
  d.fill([body[1], body[2], { x: x - 6, y: yBase - 22 }], shade(tone, 22));
  d.fill([body[3], body[4], { x: x + 12, y: yBase - 16 }], shade(tone, -18));
  speckle(d, x, yBase - 22, 28, 15, shade(tone, -26), 16, seed, 0.35);
  d.poly(body.slice(0, 5), 1.8, C.rockDark, 0.6);
}

/** The byre — an open lean-to where the working animals stand. */
function drawByre(d: Draw, st: Struct): void {
  const { x, y, w, h } = st;
  softShadow(d, x + w / 2, y + h, w * 0.5);
  ellipse(d, x + w / 2, y + h - 14, w * 0.46, 26, shade(C.sand, -20), 0.5);   // trodden floor
  // back wall and two posts
  d.rect(x + 6, y + h * 0.28, w - 12, h * 0.30, C.timber);
  grain(d, x + 6, y + h * 0.28, w - 12, h * 0.30, shade(C.timber, -40), 0.2, 20);
  d.rect(x + 8, y + h * 0.52, 11, h * 0.46, C.timberDark);
  d.rect(x + w - 19, y + h * 0.52, 11, h * 0.46, C.timberDark);
  // a shallow thatched roof
  d.fill([{ x: x - 8, y: y + h * 0.34 }, { x: x + w / 2, y }, { x: x + w + 8, y: y + h * 0.34 }], CI.straw);
  for (let i = 1; i <= 4; i++) {
    const f = i / 5;
    d.line(x + w / 2 + (x - 8 - (x + w / 2)) * f, (y + (y + h * 0.34 - y) * f),
           x + w / 2 + (x + w + 8 - (x + w / 2)) * f, (y + (y + h * 0.34 - y) * f),
           1.4, shade(CI.straw, -30), 0.4);
  }
  d.line(x - 8, y + h * 0.34, x + w + 8, y + h * 0.34, 3, shade(CI.straw, -38));
  d.line(x + w * 0.24, y + h * 0.10, x + w / 2, y + 1, 2, C.goldLight, 0.3);
  // hay and a water trough at the front
  d.rect(x + w * 0.14, y + h - 20, w * 0.26, 12, CI.straw);
  speckle(d, x + w * 0.27, y + h - 14, w * 0.14, 7, shade(CI.straw, -30), 8, x, 0.5);
}

/** A small bird on a slow loop above the grove, with its shadow on the ground. */
export function drawBird(d: Draw, x: number, yBase: number, alt: number, t: number, seed: number): void {
  const flap = Math.sin(t * 7 + seed * 5);
  const col = pick<string>(seed, 5, [shade(C.hairGray, -14), C.timberDark, '#5b4636']);
  const by = yBase - alt;
  // the shadow stays on the ground and shrinks with height — that separation is
  // the only thing that says "in the air" rather than "on the grass"
  ellipse(d, x, yBase, 6.5 - alt * 0.03, 2.4 - alt * 0.012, '#2c2417', 0.13);
  d.line(x - 1.5, by - 1, x - 10, by - 1 - flap * 6.5, 2.4, shade(col, 22));   // far wing
  ellipse(d, x, by, 5.6, 3.4, col);
  d.circle(x - 4.6, by - 1.8, 2.7, col);
  d.line(x - 7.6, by - 2, x - 10.6, by - 1.3, 1.5, C.gold);                    // beak
  d.circle(x - 5.4, by - 2.4, 0.9, C.parchment, 0.85);                         // eye
  d.line(x + 5, by + 1, x + 11.5, by + 3.5, 1.8, col);                         // tail
  d.line(x + 1.5, by - 1, x + 10, by - 1 - flap * 6.5, 2.4, shade(col, 10));   // near wing
}

// ── villagers' working props ─────────────────────────────────────────────────
/** A hoe stood in the earth. */
export function hoeProp(d: Draw, x: number, yBase: number, s = 1): void {
  d.line(x, yBase, x + 5 * s, yBase - 34 * s, 2.4 * s, C.timber);
  d.line(x + 3 * s, yBase - 30 * s, x + 12 * s, yBase - 28 * s, 2.6 * s, C.rockDark);
}

/** An upright loom with a half-woven cloth on it. */
export function loomProp(d: Draw, x: number, yBase: number, s = 1, seed = 0): void {
  softShadow(d, x, yBase, 14 * s);
  d.rect(x - 15 * s, yBase - 44 * s, 4 * s, 44 * s, C.timberDark);
  d.rect(x + 11 * s, yBase - 44 * s, 4 * s, 44 * s, C.timberDark);
  d.rect(x - 16 * s, yBase - 46 * s, 32 * s, 4 * s, C.timber);
  d.rect(x - 11 * s, yBase - 40 * s, 22 * s, 22 * s, C.cloth);
  for (let i = 0; i < 4; i++) {
    d.line(x - 11 * s, yBase - 36 * s + i * 5 * s, x + 11 * s, yBase - 36 * s + i * 5 * s, 1, shade(C.cloth, 22), 0.7);
  }
  for (let i = 0; i < 5; i++) {                                            // warp threads below the weave
    d.line(x - 9 * s + i * 4.5 * s, yBase - 18 * s, x - 9 * s + i * 4.5 * s, yBase - 6 * s, 0.9, C.parchmentEdge, 0.8);
  }
  if (hash01(seed, 3) > 0.5) d.circle(x + 18 * s, yBase - 6 * s, 5 * s, shade(C.cloth, 18));
}

/** A scroll and a low reading stand, for the rabbi. */
export function lecternProp(d: Draw, x: number, yBase: number, s = 1): void {
  softShadow(d, x, yBase, 12 * s);
  d.rect(x - 3 * s, yBase - 26 * s, 6 * s, 26 * s, C.timberDark);
  d.fill([
    { x: x - 14 * s, y: yBase - 34 * s }, { x: x + 14 * s, y: yBase - 30 * s },
    { x: x + 14 * s, y: yBase - 25 * s }, { x: x - 14 * s, y: yBase - 29 * s },
  ], C.timber);
  d.rect(x - 10 * s, yBase - 36 * s, 20 * s, 5 * s, C.parchment);
  d.line(x - 7 * s, yBase - 34 * s, x + 6 * s, yBase - 33.4 * s, 0.9, C.ink, 0.55);
}

// ── small props ───────────────────────────────────────────────────────────────
export function drawScroll(d: Draw, x: number, y: number, t: number): void {
  const bob = Math.sin(t * 2 + x) * 3;
  shadow(d, x, y + 4, 12);
  d.circle(x, y + bob, 13, C.gold, 0.25);
  d.rect(x - 9, y - 7 + bob, 18, 14, C.parchment);
  d.rect(x - 11, y - 8 + bob, 4, 16, C.parchmentEdge);
  d.rect(x + 7, y - 8 + bob, 4, 16, C.parchmentEdge);
  d.line(x - 5, y - 3 + bob, x + 5, y - 3 + bob, 1, C.ink, 0.6);
  d.line(x - 5, y + bob, x + 4, y + bob, 1, C.ink, 0.6);
  d.line(x - 5, y + 3 + bob, x + 3, y + 3 + bob, 1, C.ink, 0.6);
}

export function lampGlow(d: Draw, x: number, y: number, r: number, t: number): void {
  const f = 1 + Math.sin(t * 4 + x) * 0.06;
  d.circle(x, y, r * f, C.lamp, 0.16);
  d.circle(x, y, r * 0.5 * f, C.lamp, 0.22);
  d.circle(x, y, 5, C.white, 0.9);
}

// exclamation / interaction bubble over an NPC or node
export function marker(d: Draw, x: number, y: number, t: number, kind: 'ask' | 'deliver' | 'gather'): void {
  const bob = Math.sin(t * 3) * 3;
  const col = kind === 'deliver' ? C.leaf : kind === 'gather' ? C.gold : C.cloth;
  d.circle(x, y + bob, 11, C.white, 0.95);
  d.circle(x, y + bob, 11, col, 0.35);
  if (kind === 'ask') { d.rect(x - 1.6, y - 5 + bob, 3.2, 7, C.ink); d.circle(x, y + 5 + bob, 1.8, C.ink); }
  else if (kind === 'deliver') { d.line(x - 4, y + bob, x - 1, y + 4 + bob, 2, C.leafDark); d.line(x - 1, y + 4 + bob, x + 5, y - 4 + bob, 2, C.leafDark); }
  else { d.circle(x, y + bob, 3, col); }
}
